import React, { useState, useRef, useEffect } from "react";
import { SearchClient, VenueSearchResult, CollectionsClient, IVenuesSearchResult, SearchConfig, ISearchConfig, VenuesSearchResultGeoLocation, SavedSearchRequestClient, CurrentLocationSearchRequest, BaseSearchRequest, SavedSearchRequestWithId } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { getSearchUrl } from "Utils/getSearchUrl";

interface ISearchManager {
   locations: VenueSearchResult[],
   geoLocation: VenuesSearchResultGeoLocation | undefined,
   config: ISearchConfig,
   search: (searchOptions: BaseSearchRequest | Guid) => Promise<void>,
   cancelSearch: () => void,
   clearSearch: () => void,
   showMemberCollection: (memberCollectionId: Guid) => void,
   currentSearchLink: () => string,
   getSearchRequest: () => BaseSearchRequest,
   getSavedSearchCached: (savedSearchId: Guid, skipCache?: boolean) => Promise<SavedSearchRequestWithId>,
   readonly isSearching: boolean,
}

export const SearchContext: React.Context<ISearchManager> = React.createContext(undefined as any);
interface SearchContextProviderProps {

}

export const SearchContextProvider: React.FC<SearchContextProviderProps> = function ({ children }) {
   const [locations, setLocations] = useState<VenueSearchResult[]>([]);
   const [searchRequest, setSearchRequest] = useState<BaseSearchRequest>(new CurrentLocationSearchRequest());
   const [isSearching, setIsSearching] = useState<boolean>(false);
   const [searchAbortController, setSearchAbortController] = useState<AbortController | undefined>();
   const [memberCollectionId, setMemberCollectionId] = useState<Guid | undefined>();
   const [geoLocation, setGeoLocation] = useState<VenuesSearchResultGeoLocation | undefined>(undefined);
   const [config, setConfig] = useState<ISearchConfig>(new SearchConfig({ approvedMemberGroups: [], geofences: [], immutableOptions: undefined, amenities: [] }));
   const searchClient = useApiClient(SearchClient);
   const savedSearchRequestClient = useApiClient(SavedSearchRequestClient);
   const collectionClient = useApiClient(CollectionsClient);
   const savedSearchesCache = useRef<Record<Guid, SavedSearchRequestWithId>>({});

   useEffect(() => {
      searchClient.config().then(config => setConfig(config));
   }, []);

   const innerSearch = async (searchAction: (signal: AbortSignal) => Promise<IVenuesSearchResult>) => {
      const abortController = new AbortController();
      cancelSearch();
      setIsSearching(true);

      try {
         setSearchAbortController(abortController);
         const result = await searchAction(abortController.signal);

         setLocations(result.venues);
         setGeoLocation(result.geoLocation);

         setIsSearching(false);
         setSearchAbortController(undefined);
      }
      catch (err) {
         if (abortController.signal.aborted) {
            return
         }
         setSearchAbortController(undefined);
         setIsSearching(false);
      }
   }


   const search = async (searchOptions: BaseSearchRequest | Guid) => {
      setMemberCollectionId(undefined);

      if (typeof searchOptions == "string") {
         const savedSearchRequestId = searchOptions;

         await innerSearch(async (signal: AbortSignal) => {
            const savedSearchRequest = await getSavedSearchCached(savedSearchRequestId, true);
            setSearchRequest(savedSearchRequest.request);
            return await searchClient.executeSavedSearch(savedSearchRequestId, signal);
         });
      }
      else {
         setSearchRequest(searchOptions);
         await innerSearch(async (signal: AbortSignal) => await searchClient.execute(searchOptions, signal));
      }
   }

   const cancelSearch = () => {
      if (searchAbortController) {
         searchAbortController.abort();
         setSearchAbortController(undefined);
         setIsSearching(false);
      }
   }

   const clearSearch = () => {
      setSearchRequest(new CurrentLocationSearchRequest());
      setLocations([]);
   }

   const showMemberCollection = async (memberCollectionId: Guid) => {
      setSearchRequest(new CurrentLocationSearchRequest());
      setMemberCollectionId(memberCollectionId);
      innerSearch(async (signal: AbortSignal) => {
         const result = await collectionClient.venues(memberCollectionId, signal);
         return {
            venues: result ?? [],
            geoLocation: undefined,
            notFound: false
         };
      });
   }

   const currentSearchLink = (): string => {
      if (memberCollectionId)
         return `/collections/${memberCollectionId}`;
      else
         return getSearchUrl(searchRequest);
   }

   const getSearchRequest = (): BaseSearchRequest => {
      return BaseSearchRequest.fromJS(searchRequest.toJSON());
   }

   const getSavedSearchCached = async (savedSearchId: Guid, skipCache?: boolean): Promise<SavedSearchRequestWithId> => {
      if (!skipCache && savedSearchId in savedSearchesCache.current) {
         return savedSearchesCache.current[savedSearchId];
      }
      else {
         const result = await savedSearchRequestClient.get(savedSearchId);
         savedSearchesCache.current[savedSearchId] = result;
         return result;
      }
   }

   const searchManager: ISearchManager = {
      locations,
      geoLocation,
      config,
      search,
      isSearching,
      cancelSearch,
      clearSearch,
      showMemberCollection,
      currentSearchLink,
      getSearchRequest,
      getSavedSearchCached
   };

   return (<SearchContext.Provider value={searchManager}>
      {children}
   </SearchContext.Provider>)
}